import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../config/api';
import { formatMatchDate } from '../../utils/matchDateTime';
import { supportsSubscriberNotify } from '../../utils/subscriberTicketRules';
import { FaEdit, FaTrash, FaPlus, FaListUl, FaUsers, FaUndo } from 'react-icons/fa';
import './AdminPages.css';

const AdminTickets = () => {
  const navigate = useNavigate();
  const [tickets, setTickets] = useState([]);
  const [archivedTickets, setArchivedTickets] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchTickets();
  }, []);

  const fetchTickets = async () => {
    try {
      setLoading(true);
      const response = await api.get('/tickets?all=true&includeDeleted=true');
      const all = response.data || [];
      setTickets(all.filter((t) => !t.deletedAt));
      setArchivedTickets(all.filter((t) => t.deletedAt));
    } catch (error) {
      console.error('Error fetching tickets:', error);
      alert('Erreur lors du chargement des billets');
    } finally {
      setLoading(false);
    }
  };

  const confirmArchive = async (ticket) => {
    const warning = ticket.orderCount != null
      ? `Attention : des commandes peuvent être liées à ce billet.\n\n`
      : '';

    if (!window.confirm(
      `${warning}Archiver « ${ticket.matchTitle} » ?\n\nLe billet disparaîtra du site public mais pourra être restauré depuis la section « Billets archivés ».`,
    )) {
      return;
    }

    const typed = window.prompt(
      `Pour confirmer, saisissez exactement le nom du match :\n${ticket.matchTitle}`,
    );
    if (typed !== ticket.matchTitle) {
      if (typed !== null) {
        alert('Le nom ne correspond pas. Archivage annulé.');
      }
      return;
    }

    try {
      const { data } = await api.delete(`/tickets/${ticket._id}`);
      const orderMsg = data.orderCount > 0
        ? `\n\n${data.orderCount} commande(s) conservée(s) en base.`
        : '';
      alert(`Billet archivé.${orderMsg}`);
      fetchTickets();
    } catch (error) {
      console.error('Error archiving ticket:', error);
      alert(error.response?.data?.error || 'Erreur lors de l\'archivage');
    }
  };

  const handleRestore = async (ticket) => {
    if (!window.confirm(`Restaurer « ${ticket.matchTitle} » ?`)) return;

    try {
      await api.post(`/tickets/${ticket._id}/restore`);
      alert('Billet restauré');
      fetchTickets();
    } catch (error) {
      console.error('Error restoring ticket:', error);
      alert(error.response?.data?.error || 'Erreur lors de la restauration');
    }
  };

  const handleEdit = (ticket) => {
    navigate(`/admin/tickets/edit/${ticket._id}`);
  };

  const renderTicketRow = (ticket, { archived = false } = {}) => (
    <tr key={ticket._id} style={archived ? { opacity: 0.85 } : undefined}>
      <td>{ticket.matchTitle}</td>
      <td>{ticket.stadium}</td>
      <td>{formatMatchDate(ticket.date, 'fr-FR')}</td>
      <td>
        {!archived ? (
          <label className="toggle-switch" style={{ display: 'inline-block' }}>
            <input
              type="checkbox"
              checked={ticket.status === 'available'}
              onChange={async (e) => {
                try {
                  const newStatus = e.target.checked ? 'available' : 'full';
                  await api.put(`/tickets/${ticket._id}`, { status: newStatus });
                  fetchTickets();
                } catch (error) {
                  console.error('Error updating ticket status:', error);
                  alert(error.response?.data?.error || 'Erreur lors de la mise à jour');
                }
              }}
            />
            <span className="toggle-slider">
              {ticket.status === 'available' ? 'Disponible' : 'Complet'}
            </span>
          </label>
        ) : (
          <span className="status draft">Archivé</span>
        )}
      </td>
      <td>
        <span className={`status ${ticket.active && !archived ? 'published' : 'draft'}`}>
          {archived ? 'Archivé' : ticket.active ? 'Actif' : 'Inactif'}
        </span>
      </td>
      <td className="actions">
        {!archived && supportsSubscriberNotify(ticket) && (
          <button
            type="button"
            className="btn-edit"
            title="Abonnés / réponses"
            onClick={() => navigate(`/admin/tickets/${ticket._id}/subscriber-notify`)}
          >
            <FaUsers />
          </button>
        )}
        {!archived && (
          <button
            className="btn-edit"
            onClick={() => handleEdit(ticket)}
          >
            <FaEdit />
          </button>
        )}
        {archived ? (
          <button
            type="button"
            className="btn-edit"
            title="Restaurer"
            onClick={() => handleRestore(ticket)}
          >
            <FaUndo />
          </button>
        ) : (
          <button
            className="btn-delete"
            title="Archiver"
            onClick={() => confirmArchive(ticket)}
          >
            <FaTrash />
          </button>
        )}
      </td>
    </tr>
  );

  return (
    <div className="admin-page">
      <div className="admin-container">
        <div className="admin-page-header" style={{ flexWrap: 'wrap', gap: '1rem' }}>
          <h1>Gestion des Billets</h1>
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
            <button
              type="button"
              className="btn-edit"
              onClick={() => navigate('/admin/ticket-opponents')}
            >
              <FaListUl /> Équipes par compétition
            </button>
            <button
              type="button"
              className="btn-add"
              onClick={() => navigate('/admin/tickets/new')}
            >
              <FaPlus /> Ajouter un billet
            </button>
          </div>
        </div>

        {loading ? (
          <div className="loading">Chargement...</div>
        ) : (
          <>
            <div className="admin-table-container">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Match</th>
                    <th>Stade</th>
                    <th>Date</th>
                    <th>Disponibilité</th>
                    <th>Statut</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {tickets.map((ticket) => renderTicketRow(ticket))}
                </tbody>
              </table>
            </div>

            {archivedTickets.length > 0 && (
              <div className="admin-table-container" style={{ marginTop: '2rem' }}>
                <h2 style={{ marginBottom: '1rem', fontSize: '1.1rem' }}>Billets archivés</h2>
                <p style={{ marginBottom: '1rem', color: '#666', fontSize: '0.95rem' }}>
                  Les billets archivés sont masqués du site public. Les commandes et réponses abonnés sont conservées.
                </p>
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>Match</th>
                      <th>Stade</th>
                      <th>Date</th>
                      <th>Disponibilité</th>
                      <th>Statut</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {archivedTickets.map((ticket) => renderTicketRow(ticket, { archived: true }))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default AdminTickets;
